import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaUsers, FaPlus, FaEdit, FaTrash, FaChevronLeft, FaChevronRight, FaFilePdf, FaRobot } from 'react-icons/fa';
import Swal from 'sweetalert2';
import sociosDividendosService from '../services/sociosDividendosService';
import '../styles/pages/SociosDividendosPage.css';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

const TABS = ['Socios', 'Retiros / Remesas / Dividendos', 'Retiros en Exceso'];

const TIPO_TITULAR_LABELS = { '': 'Pleno Propietario', '1': 'Usufructuario', '2': 'Nudo Propietario' };
const TIPO_PERSONA_LABELS = { natural: 'Persona Natural', juridica: 'Persona Jurídica', fondo: 'Fondo' };

// ── Estado inicial del formulario de dividendos ───────────────────────────────
const DIVIDENDO_EMPTY = {
    anio: CURRENT_YEAR,
    fecha_retiro: '',
    rut_receptor: '',
    tipo_titular: '',
    monto_c5: '', monto_c6: '', monto_c7: '', monto_c8: '',
    monto_c9: '', monto_c10: '', monto_c11: '', monto_c12: '', monto_c13: '',
    monto_c14: '', monto_c15: '', monto_c16: '',
    credito_c17: '', credito_c18: '', credito_c19: '', credito_c20: '',
    credito_c21: '', credito_c22: '', credito_c23: '', credito_c24: '', credito_c25: '',
    credito_c26: '', credito_c27: '', credito_c28: '', credito_c29: '', credito_c30: '',
    credito_c31: '', devolucion_capital_c32: '', numero_certificado_c33: ''
};

// ── Componente principal ──────────────────────────────────────────────────────
const SociosDividendosPage = ({ token, id_company }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);

    // Socios
    const [socios, setSocios] = useState([]);
    const [loadingSocios, setLoadingSocios] = useState(false);

    // Dividendos
    const [dividendos, setDividendos] = useState([]);
    const [loadingDividendos, setLoadingDividendos] = useState(false);

    // Retiros en exceso
    const [retirosExceso, setRetirosExceso] = useState([]);
    const [loadingRetiros, setLoadingRetiros] = useState(false);

    // Modal genérico
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState(''); // 'socio' | 'dividendo' | 'retiro'
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Paginación dividendos
    const [divPage, setDivPage] = useState(1);
    const DIV_PAGE_SIZE = 10;

    // Importar socios desde PDF (IA)
    const [importando, setImportando] = useState(false);
    const [sociosPreview, setSociosPreview] = useState(null); // null = cerrado, [] = abierto
    const pdfInputRef = useRef(null);

    // ── Cargas de datos ───────────────────────────────────────────────────────

    const loadSocios = useCallback(async () => {
        setLoadingSocios(true);
        try {
            const data = await sociosDividendosService.getSocios(id_company, token);
            setSocios(data);
        } catch (e) {
            Swal.fire('Error', e.message, 'error');
        } finally {
            setLoadingSocios(false);
        }
    }, [id_company, token]);

    const loadDividendos = useCallback(async () => {
        setLoadingDividendos(true);
        try {
            const data = await sociosDividendosService.getDividendos(id_company, selectedYear, token);
            setDividendos(data);
            setDivPage(1);
        } catch (e) {
            Swal.fire('Error', e.message, 'error');
        } finally {
            setLoadingDividendos(false);
        }
    }, [id_company, selectedYear, token]);

    const loadRetirosExceso = useCallback(async () => {
        setLoadingRetiros(true);
        try {
            const data = await sociosDividendosService.getRetirosExceso(id_company, selectedYear, token);
            setRetirosExceso(data);
        } catch (e) {
            Swal.fire('Error', e.message, 'error');
        } finally {
            setLoadingRetiros(false);
        }
    }, [id_company, selectedYear, token]);

    useEffect(() => { loadSocios(); }, [loadSocios]);
    useEffect(() => { if (activeTab === 1) loadDividendos(); }, [activeTab, loadDividendos]);
    useEffect(() => { if (activeTab === 2) loadRetirosExceso(); }, [activeTab, loadRetirosExceso]);

    // ── Modal helpers ─────────────────────────────────────────────────────────

    const openModal = (type, item = null) => {
        setModalType(type);
        setEditingItem(item);
        if (type === 'socio') {
            setFormData(item
                ? { rut: item.rut, nombre: item.nombre, tipo_persona: item.tipo_persona, cantidad_acciones: item.cantidad_acciones ?? '', porcentaje_participacion: item.porcentaje_participacion ?? '', monto_clp_aportado: item.monto_clp_aportado ?? '', reajuste_31_12_2019: item.reajuste_31_12_2019 ?? '' }
                : { rut: '', nombre: '', tipo_persona: 'natural', cantidad_acciones: '', porcentaje_participacion: '', monto_clp_aportado: '', reajuste_31_12_2019: '' });
        } else if (type === 'dividendo') {
            setFormData(item ? { ...item, tipo_titular: item.tipo_titular ?? '' }
                            : { ...DIVIDENDO_EMPTY, anio: selectedYear });
        } else if (type === 'retiro') {
            setFormData(item ? { rut_beneficiario: item.rut_beneficiario, monto_retiro_exceso: item.monto_retiro_exceso ?? '' }
                            : { rut_beneficiario: '', monto_retiro_exceso: '' });
        }
        setShowModal(true);
    };

    const closeModal = () => { setShowModal(false); setEditingItem(null); setFormData({}); };

    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // ── Submit ────────────────────────────────────────────────────────────────

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (modalType === 'socio') {
                if (editingItem) await sociosDividendosService.updateSocio(editingItem.id, formData, id_company, token);
                else await sociosDividendosService.createSocio(formData, id_company, token);
                closeModal();
                loadSocios();
            } else if (modalType === 'dividendo') {
                const payload = { ...formData, anio: selectedYear,
                    tipo_titular: formData.tipo_titular === '' ? null : Number(formData.tipo_titular) };
                if (editingItem) await sociosDividendosService.updateDividendo(editingItem.id, payload, id_company, token);
                else await sociosDividendosService.createDividendo(payload, id_company, token);
                closeModal();
                loadDividendos();
            } else if (modalType === 'retiro') {
                const payload = { ...formData, anio: selectedYear };
                if (editingItem) await sociosDividendosService.updateRetiroExceso(editingItem.id, payload, id_company, token);
                else await sociosDividendosService.createRetiroExceso(payload, id_company, token);
                closeModal();
                loadRetirosExceso();
            }
        } catch (e) {
            Swal.fire('Error', e.message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────

    const handleDelete = async (type, item) => {
        const result = await Swal.fire({
            title: '¿Eliminar registro?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonText: 'Cancelar',
            confirmButtonText: 'Eliminar'
        });
        if (!result.isConfirmed) return;
        try {
            if (type === 'socio') { await sociosDividendosService.deleteSocio(item.id, id_company, token); loadSocios(); }
            else if (type === 'dividendo') { await sociosDividendosService.deleteDividendo(item.id, id_company, token); loadDividendos(); }
            else if (type === 'retiro') { await sociosDividendosService.deleteRetiroExceso(item.id, id_company, token); loadRetirosExceso(); }
        } catch (e) {
            Swal.fire('Error', e.message, 'error');
        }
    };

    // ── Importar socios desde PDF ─────────────────────────────────────────────

    const handlePdfFileSelected = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = ''; // permite re-seleccionar el mismo archivo

        setImportando(true);
        try {
            const result = await sociosDividendosService.extractSociosFromPdf(file, id_company, token);
            if (!result.success) throw new Error(result.message);
            if (result.data.length === 0) {
                Swal.fire('Sin resultados', 'No se encontraron socios en el documento.', 'warning');
                return;
            }
            setSociosPreview(result.data);
        } catch (err) {
            Swal.fire('Error al procesar PDF', err.message, 'error');
        } finally {
            setImportando(false);
        }
    };

    const handleConfirmImport = async (sociosEditados) => {
        setSociosPreview(null);
        let errores = 0;
        for (const socio of sociosEditados) {
            try {
                await sociosDividendosService.createSocio(socio, id_company, token);
            } catch {
                errores++;
            }
        }
        loadSocios();
        if (errores === 0) {
            Swal.fire('Importación exitosa', `Se importaron ${sociosEditados.length} socio(s) correctamente.`, 'success');
        } else {
            Swal.fire('Importación parcial', `Se importaron ${sociosEditados.length - errores} socio(s). ${errores} tuvieron error.`, 'warning');
        }
    };

    // ── Paginación dividendos ─────────────────────────────────────────────────
    const totalDivPages = Math.max(1, Math.ceil(dividendos.length / DIV_PAGE_SIZE));
    const pagedDividendos = dividendos.slice((divPage - 1) * DIV_PAGE_SIZE, divPage * DIV_PAGE_SIZE);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="sd-page">
            <header className="sd-header">
                <span className="sd-header-icon"><FaUsers /></span>
                <div>
                    <h1 className="sd-title">Socios Empresa y Dividendos</h1>
                    <p className="sd-subtitle">Gestión de socios y movimientos para Formulario DJ 1948</p>
                </div>
            </header>

            {/* Tabs */}
            <nav className="sd-tabs">
                {TABS.map((t, i) => (
                    <button key={i} className={`sd-tab${activeTab === i ? ' sd-tab--active' : ''}`}
                        onClick={() => setActiveTab(i)}>{t}</button>
                ))}
            </nav>

            {/* ── TAB 0: SOCIOS ─────────────────────────────────────────────── */}
            {activeTab === 0 && (
                <div className="sd-content">
                    <div className="sd-toolbar">
                        <h2 className="sd-section-title">Maestro de Socios</h2>
                        <div className="sd-toolbar-actions">
                            <input
                                ref={pdfInputRef}
                                type="file"
                                accept="application/pdf"
                                style={{ display: 'none' }}
                                onChange={handlePdfFileSelected}
                            />
                            <button
                                className="sd-btn-ai"
                                onClick={() => pdfInputRef.current?.click()}
                                disabled={importando}
                                title="Importar socios desde un PDF de estatuto o certificado societario"
                            >
                                {importando ? <><FaRobot className="sd-spin" /> Analizando...</> : <><FaFilePdf /> Importar desde PDF</>}
                            </button>
                            <button className="sd-btn-primary" onClick={() => openModal('socio')}>
                                <FaPlus /> Agregar Socio
                            </button>
                        </div>
                    </div>
                    {loadingSocios ? <p className="sd-loading">Cargando...</p> : (
                        socios.length === 0 ? (
                            <p className="sd-empty">No hay socios registrados. Agrega el primero.</p>
                        ) : (
                            <div className="sd-table-wrap">
                                <table className="sd-table">
                                    <thead>
                                        <tr>
                                            <th>RUT</th>
                                            <th>Nombre / Razón Social</th>
                                            <th>Tipo Persona</th>
                                            <th>% Participacion</th>
                                            <th>Monto CLP Aportado</th>
                                            <th>Reajuste 31/12/2019</th>
                                            <th>C4 Acciones al 31/12</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {socios.map(s => (
                                            <tr key={s.id}>
                                                <td>{s.rut}</td>
                                                <td>{s.nombre}</td>
                                                <td><span className="sd-badge">{TIPO_PERSONA_LABELS[s.tipo_persona] || s.tipo_persona}</span></td>
                                                <td>{Number(s.porcentaje_participacion || 0).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%</td>
                                                <td className="sd-cell-number">{Number(s.monto_clp_aportado || 0).toLocaleString('es-CL')}</td>
                                                <td className="sd-cell-number">{Number(s.reajuste_31_12_2019 || 0).toLocaleString('es-CL')}</td>
                                                <td>{Number(s.cantidad_acciones || 0).toLocaleString('es-CL')}</td>
                                                <td className="sd-actions">
                                                    <button className="sd-btn-icon sd-btn-edit" title="Editar" onClick={() => openModal('socio', s)}><FaEdit /></button>
                                                    <button className="sd-btn-icon sd-btn-delete" title="Eliminar" onClick={() => handleDelete('socio', s)}><FaTrash /></button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}
                </div>
            )}

            {/* ── TAB 1: DIVIDENDOS ─────────────────────────────────────────── */}
            {activeTab === 1 && (
                <div className="sd-content">
                    <div className="sd-toolbar">
                        <div className="sd-toolbar-left">
                            <h2 className="sd-section-title">Retiros / Remesas / Dividendos</h2>
                            <select className="sd-year-select" value={selectedYear}
                                onChange={e => setSelectedYear(Number(e.target.value))}>
                                {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <button className="sd-btn-primary" onClick={() => openModal('dividendo')}>
                            <FaPlus /> Agregar Registro
                        </button>
                    </div>
                    {loadingDividendos ? <p className="sd-loading">Cargando...</p> : (
                        dividendos.length === 0 ? (
                            <p className="sd-empty">No hay registros para el año {selectedYear}.</p>
                        ) : (
                            <>
                                <div className="sd-table-wrap">
                                    <table className="sd-table sd-table--dividendos">
                                        <thead>
                                            <tr>
                                                <th>C1 Fecha Retiro</th>
                                                <th>C2 Socio / Receptor</th>
                                                <th>C4 Usufruct./Nudo Prop.</th>
                                                <th>C6 Afecto c/Créd. 2017+</th>
                                                <th>Reajuste a 31-12</th>
                                                <th>C7 Afecto c/Créd. hasta 2016</th>
                                                <th>C8 IDPC Voluntario</th>
                                                <th>C9 Sin Crédito</th>
                                                <th>C34 N° Certificado</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagedDividendos.map(d => (
                                                <tr key={d.id}>
                                                    <td>{d.fecha_retiro ? d.fecha_retiro.substring(0, 10) : '—'}</td>
                                                    <td>{socios.find(s => s.rut === d.rut_receptor)?.nombre || d.rut_receptor}</td>
                                                    <td><span className="sd-badge">{TIPO_TITULAR_LABELS[String(d.tipo_titular ?? '')] || '—'}</span></td>
                                                    <td>{Number(d.monto_c5).toLocaleString('es-CL')}</td>
                                                    <td className="sd-cell-number sd-cell-calc">{Number(d.reajuste_c5_31_12 || 0).toLocaleString('es-CL')}</td>
                                                    <td>{Number(d.monto_c6).toLocaleString('es-CL')}</td>
                                                    <td>{Number(d.monto_c7).toLocaleString('es-CL')}</td>
                                                    <td>{Number(d.monto_c8).toLocaleString('es-CL')}</td>
                                                    <td>{d.numero_certificado_c33 || '—'}</td>
                                                    <td className="sd-actions">
                                                        <button className="sd-btn-icon sd-btn-edit" title="Editar" onClick={() => openModal('dividendo', d)}><FaEdit /></button>
                                                        <button className="sd-btn-icon sd-btn-delete" title="Eliminar" onClick={() => handleDelete('dividendo', d)}><FaTrash /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {totalDivPages > 1 && (
                                    <div className="sd-pagination">
                                        <button className="sd-btn-page" disabled={divPage === 1} onClick={() => setDivPage(p => p - 1)}><FaChevronLeft /></button>
                                        <span>Pág. {divPage} / {totalDivPages}</span>
                                        <button className="sd-btn-page" disabled={divPage === totalDivPages} onClick={() => setDivPage(p => p + 1)}><FaChevronRight /></button>
                                    </div>
                                )}
                            </>
                        )
                    )}
                </div>
            )}

            {/* ── TAB 2: RETIROS EN EXCESO ──────────────────────────────────── */}
            {activeTab === 2 && (
                <div className="sd-content">
                    <div className="sd-toolbar">
                        <div className="sd-toolbar-left">
                            <h2 className="sd-section-title">Retiros en Exceso (Sección C)</h2>
                            <select className="sd-year-select" value={selectedYear}
                                onChange={e => setSelectedYear(Number(e.target.value))}>
                                {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <button className="sd-btn-primary" onClick={() => openModal('retiro')}>
                            <FaPlus /> Agregar Retiro en Exceso
                        </button>
                    </div>
                    <p className="sd-help-text">
                        Registra los retiros en exceso pendientes de imputación al término del año {selectedYear} (columnas C35-C37 del DJ1948).
                    </p>
                    {loadingRetiros ? <p className="sd-loading">Cargando...</p> : (
                        retirosExceso.length === 0 ? (
                            <p className="sd-empty">No hay retiros en exceso registrados para el año {selectedYear}.</p>
                        ) : (
                            <div className="sd-table-wrap">
                                <table className="sd-table">
                                    <thead>
                                        <tr>
                                            <th>C35 RUT Beneficiario</th>
                                            <th>C37 Monto Reajustado ($)</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {retirosExceso.map(r => (
                                            <tr key={r.id}>
                                                <td>{r.rut_beneficiario}</td>
                                                <td className="sd-cell-number">{Number(r.monto_retiro_exceso).toLocaleString('es-CL')}</td>
                                                <td className="sd-actions">
                                                    <button className="sd-btn-icon sd-btn-edit" title="Editar" onClick={() => openModal('retiro', r)}><FaEdit /></button>
                                                    <button className="sd-btn-icon sd-btn-delete" title="Eliminar" onClick={() => handleDelete('retiro', r)}><FaTrash /></button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}
                </div>
            )}

            {/* ── MODAL IMPORTAR SOCIOS DESDE PDF ──────────────────────────── */}
            {sociosPreview !== null && (
                <ModalImportSocios
                    socios={sociosPreview}
                    onConfirm={handleConfirmImport}
                    onClose={() => setSociosPreview(null)}
                />
            )}

            {/* ── MODALES ───────────────────────────────────────────────────── */}
            {showModal && (
                <div className="sd-modal-overlay" onClick={closeModal}>
                    <div className="sd-modal" onClick={e => e.stopPropagation()}>
                        {modalType === 'socio' && (
                            <ModalSocio formData={formData} editing={!!editingItem}
                                onChange={handleInput} onSubmit={handleSubmit}
                                onClose={closeModal} submitting={submitting} />
                        )}
                        {modalType === 'dividendo' && (
                            <ModalDividendo formData={formData} editing={!!editingItem}
                                onChange={handleInput} onSubmit={handleSubmit}
                                onClose={closeModal} submitting={submitting} socios={socios} />
                        )}
                        {modalType === 'retiro' && (
                            <ModalRetiroExceso formData={formData} editing={!!editingItem}
                                onChange={handleInput} onSubmit={handleSubmit}
                                onClose={closeModal} submitting={submitting} socios={socios}
                                anio={selectedYear} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Modal: Importar Socios desde PDF ─────────────────────────────────────────
const TIPO_PERSONA_OPTIONS = [
    { value: 'natural', label: 'Persona Natural' },
    { value: 'juridica', label: 'Persona Jurídica' },
    { value: 'fondo', label: 'Fondo' }
];

const ModalImportSocios = ({ socios: sociosIniciales, onConfirm, onClose }) => {
    const [socios, setSocios] = useState(
        sociosIniciales.map((s, i) => ({ ...s, _key: i }))
    );

    const handleChange = (index, field, value) => {
        setSocios(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
    };

    const handleRemove = (index) => {
        setSocios(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // eslint-disable-next-line no-unused-vars
        onConfirm(socios.map(({ _key, ...rest }) => rest));
    };

    return (
        <div className="sd-modal-overlay" onClick={onClose}>
            <div className="sd-modal sd-modal--wide" onClick={e => e.stopPropagation()}>
                <h3 className="sd-modal-title">
                    <FaRobot style={{ marginRight: 8, color: '#FFC107' }} />
                    Socios detectados por IA — Revisa y confirma
                </h3>
                <p className="sd-import-hint">
                    Claude extrajo los siguientes socios del documento. Puedes editar, eliminar o confirmar antes de importar.
                </p>
                <form onSubmit={handleSubmit}>
                    <div className="sd-import-table-wrap">
                        <table className="sd-table sd-import-table">
                            <thead>
                                <tr>
                                    <th>Nombre / Razón Social</th>
                                    <th>RUT</th>
                                    <th>Tipo Persona</th>
                                    <th>% Participacion</th>
                                    <th>Monto CLP Aportado</th>
                                    <th>Acciones al 31/12</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {socios.map((s, i) => (
                                    <tr key={s._key}>
                                        <td>
                                            <input
                                                className="sd-import-input"
                                                value={s.nombre}
                                                onChange={e => handleChange(i, 'nombre', e.target.value)}
                                                required
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className="sd-import-input"
                                                value={s.rut}
                                                onChange={e => handleChange(i, 'rut', e.target.value)}
                                                placeholder="12.345.678-9"
                                            />
                                        </td>
                                        <td>
                                            <select
                                                className="sd-import-input"
                                                value={s.tipo_persona}
                                                onChange={e => handleChange(i, 'tipo_persona', e.target.value)}
                                            >
                                                {TIPO_PERSONA_OPTIONS.map(o => (
                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                className="sd-import-input sd-import-input--number"
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={s.porcentaje_participacion || 0}
                                                onChange={e => handleChange(i, 'porcentaje_participacion', parseFloat(e.target.value) || 0)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className="sd-import-input sd-import-input--number"
                                                type="number"
                                                min="0"
                                                value={s.monto_clp_aportado || 0}
                                                onChange={e => handleChange(i, 'monto_clp_aportado', parseFloat(e.target.value) || 0)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className="sd-import-input sd-import-input--number"
                                                type="number"
                                                min="0"
                                                value={s.cantidad_acciones}
                                                onChange={e => handleChange(i, 'cantidad_acciones', parseInt(e.target.value, 10) || 0)}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="sd-btn-icon sd-btn-delete"
                                                title="Eliminar"
                                                onClick={() => handleRemove(i)}
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {socios.length === 0 && (
                        <p className="sd-empty">Todos los socios fueron eliminados.</p>
                    )}
                    <div className="sd-modal-actions">
                        <button type="button" className="sd-btn-secondary" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="sd-btn-primary" disabled={socios.length === 0}>
                            <FaPlus /> Importar {socios.length} socio{socios.length !== 1 ? 's' : ''}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ── Modal: Socio ──────────────────────────────────────────────────────────────
const ModalSocio = ({ formData, editing, onChange, onSubmit, onClose, submitting }) => (
    <form onSubmit={onSubmit}>
        <h3 className="sd-modal-title">{editing ? 'Editar Socio' : 'Nuevo Socio'}</h3>
        <div className="sd-form-group">
            <label>RUT *</label>
            <input type="text" name="rut" value={formData.rut || ''} onChange={onChange}
                placeholder="Ej: 12.345.678-9" required />
        </div>
        <div className="sd-form-group">
            <label>Nombre / Razón Social *</label>
            <input type="text" name="nombre" value={formData.nombre || ''} onChange={onChange} required />
        </div>
        <div className="sd-form-group">
            <label>Tipo de Persona</label>
            <select name="tipo_persona" value={formData.tipo_persona || 'natural'} onChange={onChange}>
                <option value="natural">Persona Natural</option>
                <option value="juridica">Persona Jurídica</option>
                <option value="fondo">Fondo</option>
            </select>
        </div>
        <div className="sd-form-group">
            <label>Porcentaje de Participacion (%)</label>
            <input type="number" name="porcentaje_participacion" value={formData.porcentaje_participacion ?? ''} onChange={onChange} min="0" max="100" step="0.01" />
        </div>
        <div className="sd-form-group">
            <label>Monto en CLP Aportado ($)</label>
            <input type="number" name="monto_clp_aportado" value={formData.monto_clp_aportado ?? ''} onChange={onChange} min="0" />
        </div>
        <div className="sd-form-group">
            <label>Reajuste al 31/12/2019 ($)</label>
            <input type="number" name="reajuste_31_12_2019" value={formData.reajuste_31_12_2019 ?? ''} onChange={onChange} min="0" />
        </div>
        <div className="sd-form-group">
            <label>C4 — Cantidad de acciones al 31/12</label>
            <input type="number" name="cantidad_acciones" value={formData.cantidad_acciones ?? ''} onChange={onChange} min="0" />
        </div>
        <div className="sd-modal-actions">
            <button type="button" className="sd-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="sd-btn-primary" disabled={submitting}>
                {submitting ? 'Guardando...' : 'Guardar'}
            </button>
        </div>
    </form>
);

// ── Modal: Dividendo (formulario completo C1-C33) ─────────────────────────────
const ModalDividendo = ({ formData, editing, onChange, onSubmit, onClose, submitting, socios }) => {
    const [section, setSection] = useState(0);
    const sections = ['Identificación', 'Montos Afectos', 'Rentas Cumplidas / Exentas', 'Créditos 2017+', 'Créditos hasta 2016'];

    return (
        <form onSubmit={onSubmit}>
            <h3 className="sd-modal-title">{editing ? 'Editar Registro' : 'Nuevo Registro'} — Retiro / Remesa / Dividendo</h3>

            {/* Navegación de secciones dentro del modal */}
            <div className="sd-modal-sections">
                {sections.map((s, i) => (
                    <button key={i} type="button"
                        className={`sd-modal-section-btn${section === i ? ' active' : ''}`}
                        onClick={() => setSection(i)}>{s}</button>
                ))}
            </div>

            {/* Sección 0: Identificación (C1-C5) */}
            {section === 0 && (
                <div className="sd-form-grid">
                    <div className="sd-form-group">
                        <label>C1 — Fecha del retiro / remesa / dividendo</label>
                        <input type="date" name="fecha_retiro" value={formData.fecha_retiro ? formData.fecha_retiro.substring(0,10) : ''} onChange={onChange} />
                        <small>Para retiros en exceso: 31/12 del año anterior al informado.</small>
                    </div>
                    <div className="sd-form-group">
                        <label>C2 — Socio receptor *</label>
                        <select name="rut_receptor" value={formData.rut_receptor || ''} onChange={onChange} required>
                            <option value="">— Seleccionar socio —</option>
                            {socios.map(s => (
                                <option key={s.id} value={s.rut}>{s.nombre} ({s.rut})</option>
                            ))}
                        </select>
                        {socios.length === 0 && (
                            <small style={{ color: '#c62828' }}>No hay socios registrados. Agrega socios primero en la pestaña "Socios".</small>
                        )}
                    </div>
                    <div className="sd-form-group">
                        <label>C4 — Usufructuario beneficiario del retiro, remesa y/o dividendo distribuido</label>
                        <select name="tipo_titular" value={formData.tipo_titular ?? ''} onChange={onChange}>
                            <option value="">— Pleno Propietario (sin indicador)</option>
                            <option value="1">1 — Usufructuario</option>
                            <option value="2">2 — Nudo Propietario</option>
                        </select>
                    </div>
                </div>
            )}

            {/* Sección 1: Montos afectos a IGC/IA (C6-C9) */}
            {section === 1 && (
                <div className="sd-form-grid">
                    <div className="sd-form-group">
                        <label>C6 — Afecto IGC/IA con crédito IDPC desde 01.01.2017 ($)</label>
                        <input type="number" name="monto_c5" value={formData.monto_c5 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C7 — Afecto IGC/IA con crédito IDPC acum. hasta 31.12.2016 ($)</label>
                        <input type="number" name="monto_c6" value={formData.monto_c6 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C8 — Con derecho a crédito por IDPC voluntario ($)</label>
                        <input type="number" name="monto_c7" value={formData.monto_c7 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C9 — Sin derecho a crédito ($)</label>
                        <input type="number" name="monto_c8" value={formData.monto_c8 ?? ''} onChange={onChange} min="0" />
                    </div>
                </div>
            )}

            {/* Sección 2: Rentas con tributación cumplida / exentas / INCR (C10-C17) */}
            {section === 2 && (
                <div className="sd-form-grid">
                    <div className="sd-form-group">
                        <label>C10 — Registro RAP y diferencia inicial ex Art.14 TER A) ($)</label>
                        <input type="number" name="monto_c9" value={formData.monto_c9 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C11 — Otras rentas sin prioridad en imputación ($)</label>
                        <input type="number" name="monto_c10" value={formData.monto_c10 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C12 — Exceso distribuciones desproporcionadas ($)</label>
                        <input type="number" name="monto_c11" value={formData.monto_c11 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C13 — ISFUT Ley N°20.780 ($)</label>
                        <input type="number" name="monto_c12" value={formData.monto_c12 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C14 — Rentas hasta 31.12.1983 / ISFUT Ley N°21.210 / ISIF Ley N°21.681 ($)</label>
                        <input type="number" name="monto_c13" value={formData.monto_c13 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C15 — Rentas exentas IGC (art.11 Ley 18.401), afectas IA ($)</label>
                        <input type="number" name="monto_c14" value={formData.monto_c14 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C16 — Rentas exentas de IGC y/o IA ($)</label>
                        <input type="number" name="monto_c15" value={formData.monto_c15 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C17 — Ingresos no constitutivos de renta ($)</label>
                        <input type="number" name="monto_c16" value={formData.monto_c16 ?? ''} onChange={onChange} min="0" />
                    </div>
                </div>
            )}

            {/* Sección 3: Créditos desde 01.01.2017 (C18-C26) */}
            {section === 3 && (
                <div className="sd-form-grid">
                    <div className="sd-form-group">
                        <label>C18 — Sin dev. (no sujeto restit., hasta 31.12.2019) ($)</label>
                        <input type="number" name="credito_c17" value={formData.credito_c17 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C19 — Con dev. (no sujeto restit., hasta 31.12.2019) ($)</label>
                        <input type="number" name="credito_c18" value={formData.credito_c18 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C20 — Sin dev. (no sujeto restit., desde 01.01.2020) ($)</label>
                        <input type="number" name="credito_c19" value={formData.credito_c19 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C21 — Con dev. (no sujeto restit., desde 01.01.2020) ($)</label>
                        <input type="number" name="credito_c20" value={formData.credito_c20 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C22 — Sin dev. (sujeto a restitución) ($)</label>
                        <input type="number" name="credito_c21" value={formData.credito_c21 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C23 — Con dev. (sujeto a restitución) ($)</label>
                        <input type="number" name="credito_c22" value={formData.credito_c22 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C24 — Sin dev. (rentas exentas, sujeto restit.) ($)</label>
                        <input type="number" name="credito_c23" value={formData.credito_c23 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C25 — Con dev. (rentas exentas, sujeto restit.) ($)</label>
                        <input type="number" name="credito_c24" value={formData.credito_c24 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C26 — Crédito por IPE (desde 01.01.2017) ($)</label>
                        <input type="number" name="credito_c25" value={formData.credito_c25 ?? ''} onChange={onChange} min="0" />
                    </div>
                </div>
            )}

            {/* Sección 4: Créditos hasta 31.12.2016 + otros (C27-C34) */}
            {section === 4 && (
                <div className="sd-form-grid">
                    <div className="sd-form-group">
                        <label>C27 — Sin dev., afectas IGC/IA (hasta 31.12.2016) ($)</label>
                        <input type="number" name="credito_c26" value={formData.credito_c26 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C28 — Con dev., afectas IGC/IA (hasta 31.12.2016) ($)</label>
                        <input type="number" name="credito_c27" value={formData.credito_c27 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C29 — Sin dev., rentas exentas (hasta 31.12.2016) ($)</label>
                        <input type="number" name="credito_c28" value={formData.credito_c28 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C30 — Con dev., rentas exentas (hasta 31.12.2016) ($)</label>
                        <input type="number" name="credito_c29" value={formData.credito_c29 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C31 — Crédito por IPE (hasta 31.12.2016) ($)</label>
                        <input type="number" name="credito_c30" value={formData.credito_c30 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C32 — Crédito imp. tasa adicional ex. Art.21 LIR ($)</label>
                        <input type="number" name="credito_c31" value={formData.credito_c31 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C33 — Devolución de capital Art.17 N°7 LIR ($)</label>
                        <input type="number" name="devolucion_capital_c32" value={formData.devolucion_capital_c32 ?? ''} onChange={onChange} min="0" />
                    </div>
                    <div className="sd-form-group">
                        <label>C34 — N° Certificado</label>
                        <input
                            type="text"
                            value={editing ? (formData.numero_certificado_c33 || '—') : 'Auto-asignado al guardar'}
                            disabled
                            style={{ background: '#f5f5f5', color: '#888', fontStyle: 'italic' }}
                        />
                    </div>
                </div>
            )}

            <div className="sd-modal-actions">
                <button type="button" className="sd-btn-secondary" onClick={onClose}>Cancelar</button>
                <button type="submit" className="sd-btn-primary" disabled={submitting}>
                    {submitting ? 'Guardando...' : 'Guardar'}
                </button>
            </div>
        </form>
    );
};

// ── Modal: Retiro en Exceso ───────────────────────────────────────────────────
const ModalRetiroExceso = ({ formData, editing, onChange, onSubmit, onClose, submitting, socios, anio }) => (
    <form onSubmit={onSubmit}>
        <h3 className="sd-modal-title">{editing ? 'Editar' : 'Nuevo'} Retiro en Exceso — Año {anio}</h3>
        <div className="sd-form-group">
            <label>C35 — Beneficiario del retiro (titular o cesionario) *</label>
            <select name="rut_beneficiario" value={formData.rut_beneficiario || ''} onChange={onChange} required>
                <option value="">— Seleccionar socio —</option>
                {socios.map(s => (
                    <option key={s.id} value={s.rut}>{s.nombre} ({s.rut})</option>
                ))}
            </select>
            {socios.length === 0 && (
                <small style={{ color: '#c62828' }}>No hay socios registrados. Agrega socios primero en la pestaña "Socios".</small>
            )}
        </div>
        <div className="sd-form-group">
            <label>C37 — Monto retiro en exceso reajustado ($)</label>
            <input type="number" name="monto_retiro_exceso" value={formData.monto_retiro_exceso ?? ''}
                onChange={onChange} min="0" />
        </div>
        <div className="sd-modal-actions">
            <button type="button" className="sd-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="sd-btn-primary" disabled={submitting}>
                {submitting ? 'Guardando...' : 'Guardar'}
            </button>
        </div>
    </form>
);

export default SociosDividendosPage;
